---
id: go-shared-config
title: Shared config helpers
owner: Backend Team
status: Active
lastReviewed: 2026-02-04
visibility: internal
tags:
  - config
  - environment
  - shared
---

## Product Overview

### Who it serves

This shared package provides a common way for Go services to determine runtime environment behavior.

### Primary workflow

Service startup calls `LoadEnvs`, then runtime code checks `IsProd`/`IsDev`, reads shared config values (like `PORT`), and decodes database connection settings from Viper.

### Business rules

Production and development modes are determined by `MY_ENV` values of `prod` or `dev` (case-insensitive). Other values, including unset, are treated as non-prod/non-dev.

### Outcomes

Services can consistently choose between secret-backed configuration in prod/dev and local file-backed configuration in other environments, while reusing shared helpers for ports and Postgres credentials.

### Out of scope

Service-specific configuration loading and secrets management live in each service beyond the common environment and database helpers here.

## Technical Overview

### Architecture flow

`LoadEnvs` snapshots `MY_ENV`, stores it in memory and Viper, and then chooses a Secret Manager load path for prod/dev or a local `.env` file path for other environments. Callers then use helpers like `Port`, `IsProd`/`IsDev`, and `WritePostgreSQL`/`ReadPostgreSQL` to read shared settings from Viper.

### Entry points

`LoadEnvs` in `apps/micro-services/go-shared/config/secretmanager.go` initializes configuration, and `IsProd`/`IsDev`, `Port`, `DBDebug`, `WritePostgreSQL`, and `ReadPostgreSQL` in `apps/micro-services/go-shared/config/env.go` are the runtime helpers.

### Key modules

`secretmanager.go` handles Viper setup and Secret Manager loading; `env.go` holds environment flags and shared config helpers.

### Data sources and sinks

The package reads the `MY_ENV` environment variable, loads secrets from Google Secret Manager in prod/dev, or reads a `.env` file (default `./config`) in other environments. It exposes values via Viper, plus helpers for `PORT`, `DB_DEBUG`, and base64-encoded Postgres JSON stored under `POSTGRES_CONFIG_{ENV}` or `POSTGRES_CONFIG_{ENV}_RO`.

### Integrations and side effects

When `MY_ENV` is `prod` or `dev`, it calls Google Secret Manager and populates Viper with secret values. For non-prod/dev, it reads a local `.env` file and may panic if the config file exists but is invalid. `WritePostgreSQL` and `ReadPostgreSQL` decode base64 JSON into connection info for callers.

### Constraints and risks

If `LoadEnvs` is not called before `IsProd`/`IsDev`, the checks default to false. If `MY_ENV` is unset or mis-typed, callers will treat the runtime as non-prod/non-dev and skip Secret Manager loading. Postgres helpers return empty/errored values when the encoded config is missing or malformed.

## Changelog

- 2026-02-04: Updated behavior to reflect `env.go` helpers, shared Postgres config decoding, and current entry points.
- 2026-01-30: Initial documentation.
